import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { TmAccount } from '@/tscapi/model/account';
import { TmCart } from '@/tscapi/model/cart';
import { TmMergeCartRequest } from '@/tscapi/model/request/account_cart-request';
import { setUserToken, getAccount } from '@/tscapi/service/account.service';
import { mergeCart, getAccountCart } from '@/tscapi/service/accountcart.service';
import http from '@/tscapi/service/httpservice';
import { Cookie } from '@/tscapi/utils/cookie';

// used to check password
export const usePasswordInfo = () => {
    const base = '/api/v2/';
    const cookie: Cookie = new Cookie();
    const lang = cookie.Language.substring(0, 2) || 'en';
    type PasswordInfo = {
        Info: string;
    };

    return {
        ...useMutation({
            mutationFn: async (payload: PasswordInfo) => {
                return http.post(`${base}${lang}/passwordinfo/`, payload);
            },
            onSuccess: async (response, variables, context) => {},
            onError: (error, variables, context) => {
                const axiosError = error as any;

                if (typeof error === 'string' && error === 'LinkExpired') {
                    window.location.href = '../pages/passwordrequest?isvalid=false';
                    return;
                }
                console.log('error');
            },
        }),
    };
};

// used to check password
export const usePasswordUpdate = () => {
    const base = '/api/v2/';
    const cookie: Cookie = new Cookie();
    const lang = cookie.Language.substring(0, 2) || 'en';
    const queryClient = useQueryClient();
    type PasswordUpdate = {
        NewPassword: string;
        NewPasswordConfirmed: string;
        Info: string;
    };

    return {
        ...useMutation({
            mutationFn: async (payload: PasswordUpdate) => {
                return http.post(`${base}${lang}/passwordupdate/`, payload);
            },
            onSuccess: async (response, variables, context) => {
                setUserToken(response.data as TmAccount);
                //cart merge
                try {
                    if (cookie.CartGuid) {
                        const cartReq: TmMergeCartRequest = new TmMergeCartRequest();
                        cartReq.CartGuid = cookie.CartGuid;
                        const accountCart = await mergeCart(cartReq);
                        if (accountCart && accountCart.data) {
                            cookie.UserName = accountCart.data.Buyer.BillingAddress.FirstName;
                            cookie.LastName = accountCart.data.Buyer.BillingAddress.LastName;
                            cookie.CartGuid = accountCart.data.CartGuid;
                            cookie.CartMerge = accountCart.data.IsCartMerged.toString();
                            cookie.CartProductCount = accountCart.data.OrderSummary.TotalQuantity.toString();
                            queryClient.setQueryData(
                                ['bag', accountCart.data.CartGuid, cookie.UserAccessToken?.Edp, cookie.Language],
                                accountCart.data as TmCart,
                            );
                        }
                    } else {
                        const accountCart = await getAccountCart();
                        if (accountCart && accountCart.data) {
                            cookie.UserName = accountCart.data.Buyer.BillingAddress.FirstName;
                            cookie.LastName = accountCart.data.Buyer.BillingAddress.LastName;
                            cookie.CartGuid = accountCart.data.CartGuid;
                            cookie.CartMerge = accountCart.data.IsCartMerged.toString();
                            cookie.CartProductCount = accountCart.data.OrderSummary.TotalQuantity.toString();
                            queryClient.setQueryData(
                                ['bag', accountCart.data.CartGuid, cookie.UserAccessToken?.Edp, cookie.Language],
                                accountCart.data as TmCart,
                            );
                        }
                    }
                } catch (error) {
                    console.log(error);
                    const accountData = await getAccount();
                    if (accountData && accountData.data) {
                        cookie.UserName = accountData.data.BillingAddress.FirstName;
                        cookie.LastName = accountData.data.BillingAddress.LastName;
                    }
                }
            },
            onError: (error, variables, context) => {
                const axiosError = error as any;

                if (
                    (axiosError?.response?.status === 417 && axiosError?.response?.data?.code === 'LinkExpired') ||
                    (typeof error === 'string' && error.includes('417')) ||
                    (typeof error === 'string' && error === 'LinkExpired')
                ) {
                    window.location.href = '../pages/passwordrequest?isvalid=false';
                    return;
                }
                console.log(error);
                throw new Error(error as string);
            },
        }),
    };
};
